import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, Key, Eye, EyeOff, RefreshCw, Copy, Check, Lock, Globe, Building2, AlertTriangle, Link as LinkIcon, Settings, Shield, Calendar } from 'lucide-react';
import { vaultAPI, documentationAPI, configurationsAPI, adminCompanyAPI } from '../../services/api';

// Password type options
const passwordTypes = [
  { value: 'local_admin', label: 'Local Admin' },
  { value: 'domain_admin', label: 'Domain Admin' },
  { value: 'service_account', label: 'Service Account' },
  { value: 'api_key', label: 'API Key' },
  { value: 'wifi', label: 'WiFi' },
  { value: 'other', label: 'Other' },
];

// Password generator component
function PasswordGenerator({ onUse }) {
  const [length, setLength] = useState(16);
  const [includeUppercase, setIncludeUppercase] = useState(true);
  const [includeLowercase, setIncludeLowercase] = useState(true);
  const [includeNumbers, setIncludeNumbers] = useState(true);
  const [includeSymbols, setIncludeSymbols] = useState(true);
  const [excludeSimilar, setExcludeSimilar] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [copied, setCopied] = useState(false);

  const generatePassword = () => {
    let chars = '';
    if (includeUppercase) chars += excludeSimilar ? 'ABCDEFGHJKLMNPQRSTUVWXYZ' : 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    if (includeLowercase) chars += excludeSimilar ? 'abcdefghjkmnpqrstuvwxyz' : 'abcdefghijklmnopqrstuvwxyz';
    if (includeNumbers) chars += excludeSimilar ? '23456789' : '0123456789';
    if (includeSymbols) chars += '!@#$%^&*()_+-=[]{}|;:,.<>?';

    if (!chars) {
      chars = 'abcdefghijklmnopqrstuvwxyz';
    }

    let password = '';
    const array = new Uint32Array(length);
    crypto.getRandomValues(array);
    for (let i = 0; i < length; i++) {
      password += chars[array[i] % chars.length];
    }
    setGeneratedPassword(password);
  };

  useEffect(() => {
    generatePassword();
  }, [length, includeUppercase, includeLowercase, includeNumbers, includeSymbols, excludeSimilar]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(generatedPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getStrength = () => {
    let score = 0;
    if (length >= 12) score++;
    if (length >= 16) score++;
    if (length >= 20) score++;
    if (includeUppercase) score++;
    if (includeLowercase) score++;
    if (includeNumbers) score++;
    if (includeSymbols) score++;

    if (score <= 3) return { label: 'Weak', color: 'bg-red-500', width: '25%' };
    if (score <= 5) return { label: 'Fair', color: 'bg-yellow-500', width: '50%' };
    if (score <= 6) return { label: 'Strong', color: 'bg-blue-500', width: '75%' };
    return { label: 'Very Strong', color: 'bg-green-500', width: '100%' };
  };

  const strength = getStrength();

  return (
    <div className="bg-neutral-50 dark:bg-neutral-800/50 rounded-lg p-4 border border-neutral-200 dark:border-neutral-700">
      <h4 className="font-medium text-neutral-900 dark:text-white mb-3 flex items-center gap-2">
        <Settings className="w-4 h-4" />
        Password Generator
      </h4>

      {/* Generated password display */}
      <div className="flex items-center gap-2 mb-4">
        <div className="flex-1 font-mono text-sm bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg px-3 py-2 break-all">
          {generatedPassword}
        </div>
        <button
          type="button"
          onClick={handleCopy}
          className="p-2 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
          title="Copy"
        >
          {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
        </button>
        <button
          type="button"
          onClick={generatePassword}
          className="p-2 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
          title="Regenerate"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Strength indicator */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-neutral-500 dark:text-neutral-400">Strength</span>
          <span className="font-medium text-neutral-700 dark:text-neutral-300">{strength.label}</span>
        </div>
        <div className="h-1.5 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
          <div className={`h-full ${strength.color} transition-all`} style={{ width: strength.width }} />
        </div>
      </div>

      {/* Length slider */}
      <div className="mb-4">
        <label className="flex items-center justify-between text-sm mb-1">
          <span className="text-neutral-600 dark:text-neutral-400">Length</span>
          <span className="font-medium text-neutral-900 dark:text-white">{length}</span>
        </label>
        <input
          type="range"
          min="8"
          max="64"
          value={length}
          onChange={(e) => setLength(parseInt(e.target.value))}
          className="w-full h-2 bg-neutral-200 dark:bg-neutral-700 rounded-lg appearance-none cursor-pointer accent-brand-600"
        />
      </div>

      {/* Character options */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={includeUppercase}
            onChange={(e) => setIncludeUppercase(e.target.checked)}
            className="rounded border-neutral-300 dark:border-neutral-600 text-brand-600 focus:ring-brand-500"
          />
          <span className="text-neutral-700 dark:text-neutral-300">Uppercase (A-Z)</span>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={includeLowercase}
            onChange={(e) => setIncludeLowercase(e.target.checked)}
            className="rounded border-neutral-300 dark:border-neutral-600 text-brand-600 focus:ring-brand-500"
          />
          <span className="text-neutral-700 dark:text-neutral-300">Lowercase (a-z)</span>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={includeNumbers}
            onChange={(e) => setIncludeNumbers(e.target.checked)}
            className="rounded border-neutral-300 dark:border-neutral-600 text-brand-600 focus:ring-brand-500"
          />
          <span className="text-neutral-700 dark:text-neutral-300">Numbers (0-9)</span>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={includeSymbols}
            onChange={(e) => setIncludeSymbols(e.target.checked)}
            className="rounded border-neutral-300 dark:border-neutral-600 text-brand-600 focus:ring-brand-500"
          />
          <span className="text-neutral-700 dark:text-neutral-300">Symbols (!@#$...)</span>
        </label>
      </div>

      <label className="flex items-center gap-2 text-sm mb-4">
        <input
          type="checkbox"
          checked={excludeSimilar}
          onChange={(e) => setExcludeSimilar(e.target.checked)}
          className="rounded border-neutral-300 dark:border-neutral-600 text-brand-600 focus:ring-brand-500"
        />
        <span className="text-neutral-700 dark:text-neutral-300">Exclude similar characters (i, l, 1, L, o, 0, O)</span>
      </label>

      <button
        type="button"
        onClick={() => onUse(generatedPassword)}
        className="w-full px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors text-sm font-medium"
      >
        Use This Password
      </button>
    </div>
  );
}

export default function PasswordEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = Boolean(id);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    password: '',
    url: '',
    notes: '',
    password_type: 'other',
    visibility: 'private',
    company_id: '',
    folder_id: '',
    expires_at: '',
    rotation_reminder_days: '',
    allow_customer_reveal: false,
    otp_secret: '',
    configuration_id: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showGenerator, setShowGenerator] = useState(false);
  const [error, setError] = useState(null);

  // Fetch existing password if editing
  const { data: passwordData, isLoading: passwordLoading } = useQuery({
    queryKey: ['vault', 'password', id],
    queryFn: () => vaultAPI.getPassword(id),
    enabled: isEditing,
  });

  // Fetch folders for dropdown
  // Both calls used method names the API client never had (getFolders,
  // getConfigurations), so both dropdowns had always been empty.
  const { data: foldersData } = useQuery({
    queryKey: ['documentation', 'folders', 'flat'],
    queryFn: () => documentationAPI.listFolders(),
  });

  // Fetch configurations for linking
  const { data: configurationsData } = useQuery({
    queryKey: ['configurations'],
    queryFn: () => configurationsAPI.list(),
  });

  // Fetch companies for company selector
  const { data: companiesData } = useQuery({
    queryKey: ['admin', 'companies', 'list'],
    queryFn: () => adminCompanyAPI.list({ limit: 500 }),
  });

  const folders = foldersData?.data?.folders || [];
  const configurations = configurationsData?.data?.configurations || [];
  const companies = companiesData?.data?.companies || [];

  // Populate form when editing
  useEffect(() => {
    if (passwordData?.data) {
      const p = passwordData.data;
      setFormData({
        name: p.name || '',
        username: p.username_hint || '',
        password: '', // Don't populate - must be revealed
        url: p.url || '',
        notes: '', // Don't populate - must be revealed
        password_type: p.password_type || 'other',
        visibility: p.visibility || 'private',
        company_id: p.company_id || '',
        folder_id: p.folder_id || '',
        expires_at: p.expires_at ? p.expires_at.split('T')[0] : '',
        rotation_reminder_days: p.rotation_reminder_days || '',
        allow_customer_reveal: p.allow_customer_reveal || false,
        otp_secret: '', // Don't populate - must be revealed
        configuration_id: p.configuration_id || '',
      });
    }
  }, [passwordData]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => vaultAPI.createPassword(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['vault', 'passwords'] });
      navigate(`/vault/${response.data.id}`);
    },
    onError: (err) => {
      setError(err.response?.data?.error || 'Failed to create password');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data) => vaultAPI.updatePassword(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vault', 'passwords'] });
      queryClient.invalidateQueries({ queryKey: ['vault', 'password', id] });
      navigate(`/vault/${id}`);
    },
    onError: (err) => {
      setError(err.response?.data?.error || 'Failed to update password');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);

    // Validate required fields
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!isEditing && !formData.password) {
      setError('Password is required');
      return;
    }

    const submitData = {
      name: formData.name.trim(),
      username: formData.username.trim() || null,
      password: formData.password || undefined, // Don't send if empty on edit
      url: formData.url.trim() || null,
      notes: formData.notes.trim() || null,
      password_type: formData.password_type,
      visibility: formData.visibility,
      companyId: formData.company_id || null,
      folderId: formData.folder_id || null,
      expiresAt: formData.expires_at || null,
      rotationReminderDays: formData.rotation_reminder_days ? parseInt(formData.rotation_reminder_days) : null,
      allowCustomerReveal: formData.allow_customer_reveal,
      otpSecret: formData.otp_secret.trim() || null,
      configurationId: formData.configuration_id || null,
    };

    // Remove undefined fields for update
    if (isEditing) {
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === undefined) {
          delete submitData[key];
        }
      });
    }

    if (isEditing) {
      updateMutation.mutate(submitData);
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleUseGeneratedPassword = (password) => {
    setFormData(prev => ({ ...prev, password }));
    setShowPassword(true);
    setShowGenerator(false);
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  if (isEditing && passwordLoading) {
    return (
      <div className="animate-fade-in">
        <div className="max-w-3xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-neutral-200 dark:bg-neutral-700 rounded w-1/3" />
            <div className="h-64 bg-neutral-200 dark:bg-neutral-700 rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(isEditing ? `/vault/${id}` : '/vault')}
            className="p-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
              {isEditing ? 'Edit Password' : 'New Password'}
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              {isEditing ? 'Update credential details' : 'Add a new credential to the vault'}
            </p>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
              <Key className="w-5 h-5 text-brand-600" />
              Credential Details
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="e.g., Server Admin Login"
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white placeholder-neutral-400 focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Username */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Username
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => handleChange('username', e.target.value)}
                  placeholder="e.g., administrator"
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white placeholder-neutral-400 focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Password {!isEditing && <span className="text-red-500">*</span>}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    placeholder={isEditing ? '(unchanged)' : 'Enter password'}
                    className="w-full px-3 py-2 pr-20 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white placeholder-neutral-400 focus:ring-2 focus:ring-brand-500 focus:border-transparent font-mono"
                    required={!isEditing}
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowGenerator(!showGenerator)}
                      className="p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
                      title="Password Generator"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {isEditing && (
                  <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                    Leave blank to keep the current password
                  </p>
                )}
              </div>

              {/* URL */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  URL
                </label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => handleChange('url', e.target.value)}
                  placeholder="https://example.com/login"
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white placeholder-neutral-400 focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>

              {/* Password Type */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Password Type
                </label>
                <select
                  value={formData.password_type}
                  onChange={(e) => handleChange('password_type', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                >
                  {passwordTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Password generator */}
            {showGenerator && (
              <div className="mt-4">
                <PasswordGenerator onUse={handleUseGeneratedPassword} />
              </div>
            )}

            {/* Notes */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Additional information about this credential..."
                rows={3}
                className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white placeholder-neutral-400 focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none"
              />
              {isEditing && (
                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                  Leave blank to keep the current notes
                </p>
              )}
            </div>

            {/* OTP Secret */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                OTP Secret (TOTP)
              </label>
              <input
                type="text"
                value={formData.otp_secret}
                onChange={(e) => handleChange('otp_secret', e.target.value.toUpperCase().replace(/\s/g, ''))}
                placeholder="e.g., JBSWY3DPEHPK3PXP"
                className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white placeholder-neutral-400 focus:ring-2 focus:ring-brand-500 focus:border-transparent font-mono"
              />
              <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                Base32 encoded TOTP secret for two-factor authentication
              </p>
            </div>
          </div>

          {/* Access & Visibility */}
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-brand-600" />
              Access & Visibility
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Visibility */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Visibility
                </label>
                <div className="space-y-2">
                  <label className="flex items-start gap-3 p-3 border border-neutral-200 dark:border-neutral-700 rounded-lg cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors">
                    <input
                      type="radio"
                      name="visibility"
                      value="private"
                      checked={formData.visibility === 'private'}
                      onChange={(e) => handleChange('visibility', e.target.value)}
                      className="mt-0.5 text-brand-600 focus:ring-brand-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 font-medium text-neutral-900 dark:text-white">
                        <Lock className="w-4 h-4 text-red-500" />
                        Internal Only
                      </div>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">
                        Only technicians and admins can view
                      </p>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 p-3 border border-neutral-200 dark:border-neutral-700 rounded-lg cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors">
                    <input
                      type="radio"
                      name="visibility"
                      value="company"
                      checked={formData.visibility === 'company'}
                      onChange={(e) => handleChange('visibility', e.target.value)}
                      className="mt-0.5 text-brand-600 focus:ring-brand-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 font-medium text-neutral-900 dark:text-white">
                        <Building2 className="w-4 h-4 text-blue-500" />
                        Shared with Company
                      </div>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">
                        Visible to users from the associated company
                      </p>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 p-3 border border-neutral-200 dark:border-neutral-700 rounded-lg cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors">
                    <input
                      type="radio"
                      name="visibility"
                      value="public"
                      checked={formData.visibility === 'public'}
                      onChange={(e) => handleChange('visibility', e.target.value)}
                      className="mt-0.5 text-brand-600 focus:ring-brand-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 font-medium text-neutral-900 dark:text-white">
                        <Globe className="w-4 h-4 text-green-500" />
                        Public
                      </div>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">
                        All authenticated customers can view
                      </p>
                    </div>
                  </label>
                </div>

                {/* Company selector - show when visibility is 'company' */}
                {formData.visibility === 'company' && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Company *
                    </label>
                    <select
                      value={formData.company_id}
                      onChange={(e) => handleChange('company_id', e.target.value ? parseInt(e.target.value) : '')}
                      className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select a company...</option>
                      {companies.map(company => (
                        <option key={company.id} value={company.cw_company_id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                      This password will be visible to users from this company
                    </p>
                  </div>
                )}
              </div>

              {/* Folder */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Folder
                </label>
                <select
                  value={formData.folder_id}
                  onChange={(e) => handleChange('folder_id', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                >
                  <option value="">No folder</option>
                  {folders.map(folder => (
                    <option key={folder.id} value={folder.id}>{folder.name}</option>
                  ))}
                </select>
              </div>

              {/* Customer reveal toggle */}
              {(formData.visibility === 'company' || formData.visibility === 'public') && (
                <div className="md:col-span-2">
                  <label className="flex items-start gap-3 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.allow_customer_reveal}
                      onChange={(e) => handleChange('allow_customer_reveal', e.target.checked)}
                      className="mt-0.5 rounded border-yellow-400 text-yellow-600 focus:ring-yellow-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 font-medium text-yellow-800 dark:text-yellow-300">
                        <AlertTriangle className="w-4 h-4" />
                        Allow Customers to Reveal Password
                      </div>
                      <p className="text-sm text-yellow-700 dark:text-yellow-400">
                        When enabled, customers can see the actual password, username, and notes.
                        Otherwise, they can only see that the password exists.
                      </p>
                    </div>
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Expiration & Rotation */}
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-brand-600" />
              Expiration & Rotation
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Expiration date */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Expiration Date
                </label>
                <input
                  type="date"
                  value={formData.expires_at}
                  onChange={(e) => handleChange('expires_at', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                  Optional - set when this password should be renewed
                </p>
              </div>

              {/* Rotation reminder */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Rotation Reminder (days)
                </label>
                <input
                  type="number"
                  min="0"
                  max="365"
                  value={formData.rotation_reminder_days}
                  onChange={(e) => handleChange('rotation_reminder_days', e.target.value)}
                  placeholder="e.g., 90"
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white placeholder-neutral-400 focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                  Get reminded to rotate this password after N days
                </p>
              </div>
            </div>
          </div>

          {/* Linked Configuration */}
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
              <LinkIcon className="w-5 h-5 text-brand-600" />
              Linked Asset
            </h3>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                ConnectWise Configuration
              </label>
              <select
                value={formData.configuration_id}
                onChange={(e) => handleChange('configuration_id', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              >
                <option value="">No linked configuration</option>
                {configurations.map(config => (
                  <option key={config.id} value={config.id}>
                    {config.name} ({config.type?.name || 'Unknown Type'})
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                Link this password to a device or asset in ConnectWise
              </p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate(isEditing ? `/vault/${id}` : '/vault')}
              className="px-4 py-2 border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 disabled:bg-brand-400 text-white rounded-lg transition-colors"
            >
              {isSubmitting ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  {isEditing ? 'Saving...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditing ? 'Save Changes' : 'Create Password'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
